import {
  ArrowRed,
  Box,
  Disclaimer,
  Div,
  ErrorToast,
  GrubconLogoWhiteSimbolSmall,
  Icon,
  P,
  TooltipWrapper,
  VDriveLogoSmall,
  VGrubsDisabledIcon,
  VGrubsLogoSmall,
  VTabletLogoSmall,
  api,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { EditServices } from './EditServices';

// The services a location can have, in the order their logos stack.
const SERVICES = [
  { key: 'vDriveService', name: 'vDrive', Logo: VDriveLogoSmall },
  { key: 'vOrdersService', name: 'vTablet', Logo: VTabletLogoSmall },
  { key: 'vGrubsService', name: 'vGrubs', Logo: VGrubsLogoSmall },
  {
    key: 'vRevenueService',
    name: 'Revenue Recovery',
    Logo: GrubconLogoWhiteSimbolSmall,
  },
];

/**
 * One logo in the stack, overlapping the previous one the way the Agents
 * card's avatars do: same 32px, same white ring, same -10px overlap. The logos
 * ship at 40-42px, so they are sized here rather than in the icons, which are
 * the same shapes used elsewhere.
 */
const ServiceLogo = styled.div`
  width: 32px;
  height: 32px;
  flex: none;
  border: 2px solid ${lightTheme.white};
  border-radius: 50%;
  overflow: hidden;
  background: ${lightTheme.white};

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

export const Services = ({
  salesConfig,
  setLoading,
  id,
  setData,
  componentText,
  // The card's own label; the modal keeps the longer componentText as title.
  label,
  onServicesUpdated = () => {},
  // Opens the edit modal whenever it turns true - on mount for a system
  // problem's "Open SaaS settings" link, or later when that same action is
  // pressed on this screen - then tells the page so it drops the flag.
  openRequested = false,
  onOpenHandled = () => {},
}) => {
  const [servicesModal, setServicesModal] = useState(false);
  const [preferences, setPreferences] = useState(false);

  async function handleGetPreferences() {
    try {
      setLoading(true);
      const { data } = await api.get(`/v1/agents/preferences`);
      setPreferences(data.data);
      setServicesModal(true);
    } catch (error) {
      ErrorToast(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!openRequested) return;
    handleGetPreferences();
    onOpenHandled();
    // Keyed on the flag alone: the page clears it right after, so each
    // request opens the modal once.
  }, [openRequested]);

  const activeServices = SERVICES.filter(({ key }) => salesConfig?.[key]);

  // The card only shows logos, so the tooltip is where per-order and per-day
  // billing show.
  const tooltipFor = ({ key, name }) => {
    if (key !== 'vOrdersService') return name;
    if (salesConfig?.vOrdersPerDayFee != null)
      return `${name}, ${displayCentsInDollars(
        salesConfig.vOrdersPerDayFee
      )} per day`;
    if (salesConfig?.vOrdersPerOrderFee != null)
      return `${name}, ${displayCentsInDollars(
        salesConfig.vOrdersPerOrderFee
      )} per order`;
    return name;
  };

  return (
    <>
      <Box
        $gap="20px"
        $gapSM="10px"
        $flex
        $alignCenter
        $between
        onClick={handleGetPreferences}
        $pointer
      >
        {/* Label over the logos, the way Owner and Agents read, so a long
            label wraps above them instead of squeezing beside them. */}
        <Div $minWidth="0">
          <P $mb="4">{label ?? componentText}</P>

          {activeServices.length > 0 ? (
            <Div $flex $alignCenter>
              {activeServices.map(({ key, name, Logo }, i) => (
                <TooltipWrapper
                  key={key}
                  tooltip={tooltipFor({ key, name })}
                  style={{
                    marginLeft: i === 0 ? 0 : '-10px',
                    position: 'relative',
                    zIndex: activeServices.length - i,
                  }}
                >
                  <ServiceLogo>
                    <Logo />
                  </ServiceLogo>
                </TooltipWrapper>
              ))}
            </Div>
          ) : (
            <Div $flex $alignCenter $gap="8px">
              <VGrubsDisabledIcon />
              <Disclaimer $m="0" $color={lightTheme.lightGray}>
                No Services
              </Disclaimer>
            </Div>
          )}
        </Div>

        <Icon>
          <ArrowRed />
        </Icon>
      </Box>

      <EditServices
        preferences={preferences}
        salesConfig={salesConfig}
        modalIsOpen={servicesModal}
        setModalIsOpen={setServicesModal}
        setLoading={setLoading}
        id={id}
        setData={setData}
        componentText={componentText}
        onServicesUpdated={onServicesUpdated}
      />
    </>
  );
};
